import { Option } from 'effect';
import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';
import type { Cart as CartView } from '@vs-shop/engine/capability/cart/cartSchema';
import { CartResultSchema } from '@vs-shop/engine/module/cart/cartSnapshot';
import { reactivityKeys } from '@vs-shop/engine/atom/reactivityKeys';
import getOrder, { makeCartAtom } from '../flow/order/getOrder';

import {
  makeOrderAtoms,
  type CheckoutOrderItem,
} from '@vs-shop/engine/module/order/makeOrderAtoms';
import type { OrderAdapters } from '@vs-shop/engine/module/order/orderAdapters';
import { RuntimeClientAtom } from '@vs-shop/engine/runtime/RuntimeClient';
import { makeCartSession } from '@vs-shop/engine/module/cart/cartSession';
import { quantityOfVariantInCart } from '@vs-shop/engine/module/cart/cartState';
import { OrderDetailResultSchema } from '../schema/branded/orderTightened';
import type { OrderDetail } from '../schema/openapi/backend';
import { getOrderShipment } from '../utils/cookies/orderShipment';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import * as Atom from 'effect/unstable/reactivity/Atom';

/**
 * Seam-C (workspace#49 option C, layer 3) — order domain public barrel.
 *
 * Instantiates the engine order atom factory at module scope (the eager
 * `Atom.serializable` / `Schema.toCodecJson` sites need rcprofi's real schema
 * and runtime, which only exist here), keeps the two app-only shipment atoms,
 * and re-exports the request composition root so every order request/atom name
 * resolves through this single surface. The item adapters copy the exact
 * null-handling of the original `transformOrderItems` image logic verbatim.
 */

type OrderItemInput = OrderDetail['items'][number];

const adapters: OrderAdapters<OrderDetail, OrderItemInput> = {
  items: (detail) => detail.items,
  itemImage: (item) => {
    const image = item.variant.product.mainImage;

    return image !== null
      ? {
          path: image?.pathAbstract
            ? resolveImageUrl(image.pathAbstract, ImageFilter.product_card_cart)
            : '', // TODO (verbatim from transformOrderItems)
          type: image?.type ?? null,
        }
      : null;
  },
};

const orderModule = makeOrderAtoms({
  orderDetailResultSchema: OrderDetailResultSchema,
  getOrder: () => getOrder,
  adapters,
  runtime: RuntimeClientAtom,
  reactivityKey: reactivityKeys.orderToken,
  atomKey: 'order',
});

export const orderAtom = orderModule.orderAtom;
export const knownOrderAtom = orderModule.knownOrderAtom;
export const checkoutOrderItemsAtom = orderModule.checkoutOrderItemsAtom;
export const encodeOrderDetailResult = orderModule.encodeOrderDetailResult;
export const orderDetailResultJsonCodec = orderModule.orderDetailResultJsonCodec;

export { cartResultJsonCodec, encodeCartResult } from '@vs-shop/engine/module/cart/cartSnapshot';

export const cartAtomKey = 'cart';

export const cartAtom = makeCartAtom(orderAtom).pipe(
  Atom.serializable({ key: cartAtomKey, schema: CartResultSchema }),
  Atom.keepAlive
);

export const cartSession = makeCartSession(cartAtom);
export const cartState = cartSession.state;
export const knownCartAtom = cartState.knownCartAtom;

export const cartDomainItemsAtom = Atom.map(knownCartAtom, (cartOption) =>
  Option.match(cartOption, {
    onNone: () => [],
    onSome: (cart) => cart.items,
  })
);

export const cartDomainIsEmptyAtom = Atom.map(knownCartAtom, (cartOption) =>
  Option.match(cartOption, {
    onNone: () => true,
    onSome: (cart) => cart.items.length === 0,
  })
);

export const cartDomainQuantityInCartByProductVariantCodeAtom = Atom.family((variantCode: string) =>
  Atom.map(knownCartAtom, (cartOption) =>
    Option.match(cartOption, {
      onNone: () => 0,
      onSome: (cart) => quantityOfVariantInCart(cart, variantCode),
    })
  )
);

export type CartLine = CartView['items'][number];
export type CartLines = CartView['items'];

/** Cart-item view-model types follow the engine checkout item schema. */
export type OrderItems = CheckoutOrderItem<OrderItemInput>[];

export type OrderItem = OrderItems[number];

// --- app-only order-shipment atoms (no order-schema coupling) --------------------

export type { OrderShipmentStorageT } from '../service/orderShipment/OrderShipment';

export const getOrderShipmentAtom = RuntimeClientAtom.atom(getOrderShipment).pipe(
  Atom.withReactivity([reactivityKeys.orderShipment]),
  Atom.withServerValueInitial
);

export const orderShipmentAtom = Atom.map(getOrderShipmentAtom, (orderShipmentResult) =>
  AsyncResult.match(orderShipmentResult, {
    onInitial: () => null,
    onFailure: () => null,
    onSuccess: (success) => success.value,
  })
).pipe(Atom.withReactivity([reactivityKeys.orderShipment]));

// --- request composition root (leaf; re-exported for a single import surface) ----

export * from './orderRequests';
